import { useState } from 'react'
import { useSelector } from 'react-redux'
import {
  selectCurrentWorkspace,
  selectWorkspaces,
  selectInvitations,
} from '../../store/slices/workspaceSlice'
import { CreateInvitation } from './CreateInvitation'
import { InvitationStatus } from '../../lib/db/db.types'
import { usePermissions } from '../../store/hooks'

const statusToShow: InvitationStatus[] = [
  'created',
  'pending',
  'error',
  'expired',
]

export function Collaborators() {
  const [showInviteDialog, setShowInviteDialog] = useState(false)
  const currentWorkspaceId = useSelector(selectCurrentWorkspace)
  const workspaces = useSelector(selectWorkspaces)
  const allInvitations = useSelector(selectInvitations)

  const { isOwner } = usePermissions()

  const currentWorkspace = workspaces.find((w) => w.id === currentWorkspaceId)
  const collaborators = currentWorkspace?.collaborators ?? {}

  const workspaceInvitations = currentWorkspaceId
    ? (allInvitations[currentWorkspaceId] ?? [])
    : []

  const invitations = workspaceInvitations.filter((invitation) =>
    statusToShow.includes(invitation.status),
  )

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold">Team members</h2>
        {isOwner && (
          <button
            onClick={() => setShowInviteDialog(true)}
            className="bg-blue-500 text-white px-4 py-2 rounded"
          >
            Invite team member
          </button>
        )}
      </div>

      <div className="mb-8">
        {invitations.length > 0 && (
          <h3 className="text-lg font-semibold mb-4">Current Team Members</h3>
        )}
        {Object.keys(collaborators).length === 0 ? (
          <div className="flex flex-col items-start justify-center h-full">
            <p className="text-gray-500">
              No team members. {isOwner && 'Invite someone to join your team.'}
            </p>
          </div>
        ) : (
          <ul className="space-y-2">
            {Object.entries(collaborators).map(([userId, collaborator]) => (
              <li
                key={userId}
                className="flex items-center justify-between border p-3 rounded"
              >
                <div>
                  <p className="font-medium">{collaborator.name}</p>
                  <p className="text-gray-600">{collaborator.email}</p>
                </div>
                <div className="flex gap-2">
                  {collaborator.permissions.isOwner && (
                    <span className="text-sm bg-purple-100 text-purple-800 px-2 py-1 rounded">
                      Owner
                    </span>
                  )}
                  {collaborator.permissions.canManageEvents && (
                    <span className="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded">
                      Manage Events
                    </span>
                  )}
                  {collaborator.permissions.canManageReminders && (
                    <span className="text-sm bg-green-100 text-green-800 px-2 py-1 rounded">
                      Manage Reminders
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {invitations.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-4">Pending Invitations</h3>
          <ul className="space-y-2">
            {invitations.map((invitation) => (
              <li
                key={invitation.id}
                className="flex items-center justify-between border p-3 rounded"
              >
                <div>
                  <p className="text-gray-600">{invitation.email}</p>
                  <p
                    className={`text-sm capitalize ${
                      invitation.status === 'error'
                        ? 'text-red-600 font-medium'
                        : 'text-gray-500'
                    }`}
                  >
                    {invitation.status}
                  </p>
                </div>
                <div className="flex gap-2">
                  {invitation.permissions.canManageEvents && (
                    <span className="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded">
                      Manage Events
                    </span>
                  )}
                  {invitation.permissions.canManageReminders && (
                    <span className="text-sm bg-green-100 text-green-800 px-2 py-1 rounded">
                      Manage Reminders
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {showInviteDialog && currentWorkspaceId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Invite Team Member</h3>
              <button
                onClick={() => setShowInviteDialog(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ×
              </button>
            </div>
            <CreateInvitation workspaceId={currentWorkspaceId} />
          </div>
        </div>
      )}
    </div>
  )
}
